import { SofReceivingOrder } from '../../../models/sof-receiving/SofReceivingOrder.interface';

/**
 * The searchable text for one SOF, assembled by the caller. PO numbers do not
 * live on the queue row — they arrive with the per-SOF meta fan-out — so they
 * are passed in rather than read off the order.
 */
export interface SofSearchText {
  /** Every PO number on the SOF, from its queue meta. */
  poNumbers: string[];
}

/** Collapses a value to a comparable form: lowercase, no surrounding space. */
function norm(value: string | null | undefined): string {
  return (value ?? '').trim().toLowerCase();
}

/**
 * Matches one order against a search term.
 *
 * Three things are searchable, per the ask: SOF number, job number, and PO
 * number. The job number is not a field of its own — the view composes
 * `orderTo` as `JOB: <number>  <name>` — so matching `orderTo` covers both the
 * job number and the job name, which is a superset of what was asked for and
 * costs nothing.
 *
 * Substring rather than prefix or exact: warehouse staff read a partial number
 * off a slip and type the middle of it as often as the start.
 *
 * @param order - The queue row.
 * @param search - PO numbers for this SOF, from its meta (may be absent while
 *                 the meta fan-out is still in flight).
 * @param term - The raw search box value.
 * @returns True when the order should be shown.
 */
export function orderMatchesSearch(
  order: SofReceivingOrder,
  search: SofSearchText | null | undefined,
  term: string
): boolean {
  const needle = norm(term);
  if (!needle) {
    return true;
  }

  if (norm(order.sof).includes(needle)) {
    return true;
  }
  if (norm(order.orderTo).includes(needle)) {
    return true;
  }

  // Meta loads after the queue does, so a SOF whose POs haven't arrived yet
  // simply can't match on PO — it still matches on SOF or job.
  return (search?.poNumbers ?? []).some((po) => norm(po).includes(needle));
}

/**
 * Filters the queue by a search term, preserving the incoming order (which the
 * caller has already sorted). A blank term returns the list unchanged.
 *
 * Pure — returns a new array; does not mutate the input.
 *
 * @param orders - The sorted queue rows.
 * @param searchBySof - PO numbers keyed by SOF number.
 * @param term - The raw search box value.
 * @returns The matching orders, in their original relative order.
 */
export function filterOrders(
  orders: SofReceivingOrder[],
  searchBySof: Record<string, SofSearchText>,
  term: string
): SofReceivingOrder[] {
  if (!norm(term)) {
    return [...orders];
  }
  return orders.filter((order) =>
    orderMatchesSearch(order, searchBySof?.[order.sof], term)
  );
}
